import React from 'react';
import styled from 'styled-components';
import { ScreenContainer } from '../../components/containers/containers';
import CenteredSpinner from '../../components/loading-indicators/centered-spinner';
import HeaderBackButton from '../../components/ui-utils/screen-header/header-back-button';
import ScreenHeader from '../../components/ui-utils/screen-header/screen-header';

const StepsContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-around;
  @media (min-width: 1280px) {
    width: unset;
    justify-content: unset;
  }
`;

const StepItemContainer = styled.div`
  display: flex;
  align-items: center;
  max-width: 160px;
  flex-direction: column;
  margin-right: 0px;
  flex: 1;
  &:last-child {
    margin-right: 0px;
  }
  @media (min-width: 1280px) {
    flex-direction: row;
    margin-right: 50px;
    height: 56px;
    flex: unset;
  }
`;

const HeaderContainer = styled.header`
  margin-bottom: 20px;
  position: relative;
  @media (min-width: 1280px) {
    margin-bottom: 30px;
  }
`;

const StepIndexContainer = styled.div.attrs(() => ({}))<{
  color: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  background-color: var(--matt-bg-color);
  height: 56px;
  width: 56px;
  margin-bottom: 10px;
  border: 2px solid ${(props) => props.color};
  border-radius: 2px;
  @media (min-width: 1280px) {
    margin-right: 16px;
    margin-bottom: 0px;
  }
`;

const StepItem = (props: {
  index: number;
  label: string;
  status: 'active' | 'next' | 'done';
}): JSX.Element => {
  let color = 'var(--gray-text-color)';
  switch (props.status) {
    case 'done':
      color = 'var(--success-color)';
      break;
    case 'active':
      color = 'var(--primary-color)';
      break;
    case 'next':
      color = 'var(--gray-text-color)';
      break;
  }
  return (
    <StepItemContainer>
      <StepIndexContainer color={color}>
        <h1 className={props.status === 'next' ? 'text-gray' : ''}>{props.index}</h1>
      </StepIndexContainer>
      <p className="text-14 text-center font-normal md:font-bold">{props.label}</p>
    </StepItemContainer>
  );
};

const getStepStatus = (index: number, currentIndex: number): 'active' | 'next' | 'done' => {
  if (index < currentIndex) {
    return 'done';
  } else if (index > currentIndex) {
    return 'next';
  } else {
    return 'active';
  }
};

const ImageBackground = styled.img`
  height: 150px;
  width: 100%;
  object-fit: cover;
  position: absolute;
  top: 0px;
  left: 0px;
`;

const DefaultScreen = (props: {
  children: React.ReactNode;
  loading?: boolean;
  background: string;
  title: string;
  updatedAt?: string;
  backUrl?: string;
  onBackClick?: () => void;
  id?: string;
  steps?: string[];
  currentStep?: number;
  rightContent?: JSX.Element;
}): JSX.Element => {
  return (
    <ScreenContainer id={props.id}>
      <ImageBackground src={props.background} />
      <HeaderContainer className="flex flex-col justify-between md:flex-row">
        <div className="flex flex-row items-center mb-3 md:mb-0">
          {props.backUrl || props.onBackClick ? (
            <HeaderBackButton url={props.backUrl} onClick={props.onBackClick} />
          ) : null}
          <ScreenHeader title={props.title} lastUpdate={props.updatedAt} />
        </div>
        {props.rightContent}
        {props.steps ? (
          <StepsContainer>
            {props.steps.map((step: string, index: number) => (
              <StepItem
                key={index}
                index={index + 1}
                label={step}
                status={getStepStatus(index, props.currentStep || 0)}
              />
            ))}
          </StepsContainer>
        ) : null}
      </HeaderContainer>
      <div className="w-full relative">
        {props.loading ? <CenteredSpinner className="mt-12" /> : props.children}
      </div>
    </ScreenContainer>
  );
};

export default DefaultScreen;
